import React, { useState, useEffect } from 'react';
import { Product, Company, InvoiceItem, Invoice } from '../types';
import { getProducts, getCompanies } from '../services/api';
import { 
  generateRandomCustomer, 
  generateOrderNumber, 
  generateInvoiceNumber, 
  generateTrackingNumber
} from '../utils/generators';
import './InvoiceForm.css';

interface Props {
  onGeneratePDF: (invoices: Invoice[]) => void;
}

interface ItemWithPages extends InvoiceItem {
  pageCount: number;
}

const InvoiceForm: React.FC<Props> = ({ onGeneratePDF }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedSKU, setSelectedSKU] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [discount, setDiscount] = useState<number>(0);
  const [deliveryType, setDeliveryType] = useState<'COD' | 'PREPAID'>('COD');
  const [pageCount, setPageCount] = useState<number>(1);
  const [itemsWithPages, setItemsWithPages] = useState<ItemWithPages[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [productsData, companiesData] = await Promise.all([
        getProducts(),
        getCompanies()
      ]);
      
      setProducts(productsData);
      setCompanies(companiesData);
      setSelectedCompany(null);
    } catch (error) {
      alert(`Failed to load data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter products by selected company
  const filteredProducts = selectedCompany 
    ? products.filter(p => p.companyId === (selectedCompany._id || selectedCompany.id))
    : [];

  const selectedProduct = filteredProducts.find(p => p.sku === selectedSKU);

  const handleAddItem = () => {
    if (!selectedProduct || !selectedSize || !selectedColor) {
      alert('Please select all product details');
      return;
    }

    if (pageCount < 1) {
      alert('Page count must be at least 1');
      return;
    }

    const newItem: ItemWithPages = {
      product: selectedProduct,
      quantity,
      size: selectedSize,
      color: selectedColor,
      discount,
      pageCount
    };

    setItemsWithPages([...itemsWithPages, newItem]);
    
    // Reset form
    setSelectedSKU('');
    setQuantity(1);
    setSelectedSize('');
    setSelectedColor('');
    setDiscount(0);
    setPageCount(1);
  };

  const handleRemoveItem = (index: number) => {
    setItemsWithPages(itemsWithPages.filter((_, i) => i !== index));
  };

  const getTotalPages = () => {
    return itemsWithPages.reduce((sum, item) => sum + item.pageCount, 0);
  };

  const handleGenerateInvoices = () => {
    if (itemsWithPages.length === 0) {
      alert('Please add at least one item');
      return;
    }

    if (!selectedCompany) {
      alert('Please select a company');
      return;
    }

    const invoices: Invoice[] = [];
    
    // Map to store state per SKU - all pages with same SKU get same state
    const skuStateMap = new Map<string, string>();
    
    // Generate invoices for each item based on its page count
    itemsWithPages.forEach((itemWithPages) => {
      const { pageCount: pages, ...item } = itemWithPages;
      const sku = item.product.sku;
      
      // Generate state once per SKU if not already generated
      if (!skuStateMap.has(sku)) {
        skuStateMap.set(sku, generateRandomCustomer().state);
      }
      
      const stateForThisSKU = skuStateMap.get(sku)!;
      
      for (let i = 0; i < pages; i++) {
        const customer = generateRandomCustomer(stateForThisSKU);
        const orderNumber = generateOrderNumber();
        const invoiceNumber = generateInvoiceNumber();
        const trackingNumber = generateTrackingNumber();
        
        // Get current date in UTC to avoid timezone issues
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const day = now.getDate();
        // Create UTC date at midnight
        const currentDate = new Date(Date.UTC(year, month, day));

        const invoice: Invoice = {
          orderNumber,
          invoiceNumber,
          trackingNumber,
          customer,
          company: selectedCompany,
          items: [item], // Each invoice has only this item
          orderDate: currentDate.toISOString(),
          invoiceDate: currentDate.toISOString(),
          deliveryType
        };

        invoices.push(invoice);
      }
    });

    onGeneratePDF(invoices);
    
    // Reset form after generation
    setItemsWithPages([]);
    setDeliveryType('COD');
  };

  if (loading) {
    return <div className="invoice-form"><p>Loading...</p></div>;
  }

  return (
    <div className="invoice-form">
      <h1>Invoice Generator</h1>
      
      {/* Company Selection */}
      <div className="form-section">
        <h2>Company Details</h2>
        <div className="form-group">
          <label htmlFor="company">Select Company:</label>
          <select
            id="company"
            value={selectedCompany?._id || selectedCompany?.id || ''}
            onChange={(e) => {
              const company = companies.find(c => (c._id || c.id) === e.target.value);
              setSelectedCompany(company || null);
              // Reset product selection when company changes
              setSelectedSKU('');
              setSelectedSize('');
              setSelectedColor('');
            }}
            className="form-control"
          >
            <option value="">Select a company</option>
            {companies.map((company) => (
              <option key={company._id || company.id} value={company._id || company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="deliveryType">Delivery Type:</label>
          <select
            id="deliveryType"
            value={deliveryType}
            onChange={(e) => setDeliveryType(e.target.value as 'COD' | 'PREPAID')}
            className="form-control"
          >
            <option value="COD">Cash on Delivery (COD)</option>
            <option value="PREPAID">Prepaid</option>
          </select>
        </div>
      </div>

      {/* Add Product Item */}
      <div className="form-section">
        <h2>Add Product Item</h2>
        
        <div className="form-group">
          <label htmlFor="sku">Select Product (SKU):</label>
          <select
            id="sku"
            value={selectedSKU}
            onChange={(e) => {
              setSelectedSKU(e.target.value);
              setSelectedSize('');
              setSelectedColor('');
            }}
            className="form-control"
            disabled={!selectedCompany}
          >
            <option value="">
              {!selectedCompany 
                ? 'Please select a company first' 
                : filteredProducts.length === 0 
                  ? 'No products available for this company'
                  : 'Select a product'}
            </option>
            {filteredProducts.map((product) => (
              <option key={product.sku} value={product.sku}>
                {product.sku} - {product.name}
              </option>
            ))}
          </select>
        </div>

        {selectedProduct && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="size">Size:</label>
                <select
                  id="size"
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  className="form-control"
                >
                  <option value="">Select size</option>
                  {selectedProduct.sizes.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="color">Color:</label>
                <select
                  id="color"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="form-control"
                >
                  <option value="">Select color</option>
                  {selectedProduct.colors.map((color) => (
                    <option key={color} value={color}>{color}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quantity">Quantity:</label>
                <input
                  type="text"
                  id="quantity"
                  value={quantity === 0 ? '' : quantity}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '0') {
                      setQuantity(0);
                    } else if (/^\d+$/.test(val)) {
                      setQuantity(parseInt(val));
                    }
                  }}
                  onBlur={() => {
                    if (quantity === 0) {
                      setQuantity(1);
                    }
                  }}
                  placeholder="1"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="discount">Discount (%):</label>
                <input
                  type="text"
                  id="discount"
                  value={discount === 0 ? '' : discount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '0') {
                      setDiscount(0);
                    } else if (/^\d*\.?\d*$/.test(val)) {
                      const num = parseFloat(val);
                      if (!isNaN(num) && num <= 100) {
                        setDiscount(num);
                      }
                    }
                  }}
                  onBlur={() => {
                    if (discount > 100) {
                      setDiscount(100);
                    }
                  }}
                  placeholder="0"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="pageCount">Number of Pages:</label>
                <input
                  type="text"
                  id="pageCount"
                  value={pageCount === 0 ? '' : pageCount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '0') {
                      setPageCount(0);
                    } else if (/^\d+$/.test(val)) {
                      setPageCount(parseInt(val));
                    }
                  }}
                  onBlur={() => {
                    if (pageCount === 0) {
                      setPageCount(1);
                    }
                  }}
                  placeholder="1"
                  className="form-control"
                />
              </div>
            </div>

            <button onClick={handleAddItem} className="btn btn-primary">
              Add Item
            </button>
          </>
        )}
      </div>

      {/* Items List */}
      {itemsWithPages.length > 0 && (
        <div className="form-section">
          <h2>Added Items (Total Pages: {getTotalPages()})</h2>
          <div className="items-list">
            {itemsWithPages.map((item, index) => (
              <div key={index} className="item-card">
                <div className="item-details">
                  <h3>{item.product.name}</h3>
                  <p><strong>SKU:</strong> {item.product.sku}</p>
                  <p><strong>Size:</strong> {item.size} | <strong>Color:</strong> {item.color}</p>
                  <p><strong>Quantity:</strong> {item.quantity} | <strong>Discount:</strong> {item.discount}%</p>
                  <p><strong>Pages:</strong> {item.pageCount}</p>
                  <p><strong>Price:</strong> ₹{item.product.price} | <strong>GST:</strong> {item.product.gstRate}%</p>
                </div>
                <button 
                  onClick={() => handleRemoveItem(index)} 
                  className="btn btn-danger"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generate Button */}
      <div className="form-section">
        <button 
          onClick={handleGenerateInvoices} 
          className="btn btn-success btn-large"
          disabled={itemsWithPages.length === 0}
        >
          Generate PDF ({getTotalPages()} pages)
        </button>
      </div>
    </div>
  );
};

export default InvoiceForm;
